/**
 * Nextdoor Share Plugin (ShareKit) — https://nextdoor.com/sharekit/
 *
 * This is the open, no-auth share widget, NOT the Nextdoor Publish API: no API
 * key, OAuth connection, secret, or backend integration exists or is needed.
 * The user completes the post manually on Nextdoor after this opens their
 * ShareKit compose window with the body pre-filled.
 *
 * Kept as a small standalone module (not baked into ContentShareBar.tsx)
 * so entity pages with their own bespoke action-button UI (certificates,
 * achievements) can trigger a Nextdoor share without rendering the full
 * multi-destination share menu.
 */

export type NextdoorEntityType =
  | 'story'
  | 'achievement'
  | 'community'
  | 'community_post'
  | 'ledger_record'
  | 'place'
  | 'profile';

export type NextdoorShareInput = {
  entityType?: NextdoorEntityType;
  /** Title, name, or headline of the thing being shared. */
  title: string;
  /** The canonical, public, no-auth-required Ertzyx URL for this entity. */
  publicUrl: string;
};

const NEXTDOOR_SHAREKIT_BASE_URL = 'https://nextdoor.com/sharekit/';
const NEXTDOOR_SOURCE = 'ertzyx';
const NEXTDOOR_HASH_TAG = 'Ertzyx';

// Nextdoor's own cap is 3,500 characters on the (URL-encoded) body param.
// This stays comfortably under that so a title with heavily-encoded
// characters (unicode, punctuation) never risks tipping over the real limit.
const NEXTDOOR_MAX_ENCODED_BODY_LENGTH = 3000;
const NEXTDOOR_MAX_TITLE_LENGTH = 220;
const NEXTDOOR_MIN_TITLE_LENGTH = 20;

function truncate(text: string, maxLength: number): string {
  const trimmed = text.trim();
  if (trimmed.length <= maxLength) return trimmed;
  return `${trimmed.slice(0, Math.max(0, maxLength - 1)).trimEnd()}…`;
}

function templateFor(entityType: NextdoorEntityType | undefined, title: string, publicUrl: string): string {
  switch (entityType) {
    case 'story':
      return `I preserved this story on Ertzyx:\n\n${title}\n\n${publicUrl}`;
    case 'achievement':
      return `I shared this achievement on Ertzyx:\n\n${title}\n\n${publicUrl}`;
    case 'community':
      return `Explore this community on Ertzyx:\n\n${title}\n\n${publicUrl}`;
    case 'place':
      return `Explore this place and its stories on Ertzyx:\n\n${title}\n\n${publicUrl}`;
    case 'ledger_record':
      return `I verified this record on Ertzyx:\n\n${title}\n\n${publicUrl}`;
    case 'community_post':
      return `Shared from Ertzyx:\n\n${title}\n\n${publicUrl}`;
    case 'profile':
      return `Check out this living record on Ertzyx:\n\n${title}\n\n${publicUrl}`;
    default:
      return `${title}\n\n${publicUrl}`;
  }
}

function encodedLength(body: string): number {
  // Measured via the same encoding URLSearchParams itself applies (not
  // encodeURIComponent, which differs slightly, e.g. space handling) so the
  // budget check matches what actually goes over the wire.
  return new URLSearchParams({ body }).toString().length;
}

/**
 * Builds the ShareKit body text, truncating the title/name (never the URL,
 * which is always the final, complete, working link) until the encoded
 * length is comfortably under Nextdoor's cap.
 */
export function buildNextdoorShareBody({ entityType, title, publicUrl }: NextdoorShareInput): string {
  let safeTitle = truncate(title, NEXTDOOR_MAX_TITLE_LENGTH);
  let body = templateFor(entityType, safeTitle, publicUrl);

  while (
    encodedLength(body) > NEXTDOOR_MAX_ENCODED_BODY_LENGTH &&
    safeTitle.length > NEXTDOOR_MIN_TITLE_LENGTH
  ) {
    safeTitle = truncate(safeTitle, safeTitle.length - 20);
    body = templateFor(entityType, safeTitle, publicUrl);
  }

  return body;
}

/** Pure URL builder — uses URL/URLSearchParams, never string concatenation. */
export function buildNextdoorShareUrl(input: NextdoorShareInput): URL {
  const body = buildNextdoorShareBody(input);
  const url = new URL(NEXTDOOR_SHAREKIT_BASE_URL);
  url.searchParams.set('source', NEXTDOOR_SOURCE);
  url.searchParams.set('body', body);
  url.searchParams.set('hash_tag', NEXTDOOR_HASH_TAG);
  return url;
}

/**
 * Opens the Nextdoor ShareKit compose window in a new tab. Never replaces
 * the current Ertzyx route (always `_blank`). If the popup is blocked,
 * copies the ShareKit link to the clipboard instead of silently failing or
 * navigating the current tab away from Ertzyx, and reports the outcome via
 * `onResult` so callers can surface their own toast (matching how the rest
 * of the share surfaces in this codebase handle feedback).
 */
export function openNextdoorShare(
  input: NextdoorShareInput,
  onResult?: (result: 'opened' | 'popup_blocked' | 'popup_blocked_copy_failed') => void
): void {
  const shareUrl = buildNextdoorShareUrl(input);
  const popup = window.open(shareUrl.toString(), '_blank', 'noopener,noreferrer');

  if (popup) {
    onResult?.('opened');
    return;
  }

  if (typeof navigator !== 'undefined' && navigator.clipboard) {
    navigator.clipboard
      .writeText(shareUrl.toString())
      .then(() => onResult?.('popup_blocked'))
      .catch(() => onResult?.('popup_blocked_copy_failed'));
  } else {
    onResult?.('popup_blocked_copy_failed');
  }
}
